// src/components/shared/FormComponents.js
import React, { useState, useEffect } from 'react';
export const Select = ({ label, value, onChange, options, placeholder = '', className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
    <select 
      className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm text-sm sm:text-base" 
      value={value} 
      onChange={onChange}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(opt => (
        <option key={opt.value || opt} value={opt.value || opt}>
          {opt.label || opt}
        </option>
      ))}
    </select>
  </div>
);

export const NumberInput = ({ label, value, onChange, min = 0, max = 999, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
    <input
      type="number"
      min={min}
      max={max}
      value={value === 0 ? '' : value}
      onChange={onChange}
      placeholder="0"
      className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm text-sm sm:text-base"
    />
  </div>
);

export const SearchableDropdown = ({ 
  label, 
  searchTerm, 
  onSearchChange, 
  onSelect, 
  options, 
  isOpen, 
  onToggle, 
  placeholder = 'Search...', 
  className = '' 
}) => (
  <div className={className}>
    <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
    <div className="relative">
      <input
        type="text"
        placeholder={placeholder}
        value={searchTerm}
        onChange={onSearchChange}
        onFocus={() => onToggle(true)}
        onBlur={(e) => {
          // Only close if not clicking on a dropdown option
          setTimeout(() => onToggle(false), 150);
        }}
        className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm text-sm sm:text-base"
      />
      {isOpen && options.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {options.slice(0, 100).map((option, index) => (
            <button
              key={option}
              onClick={() => onSelect(option)}
              className={`w-full px-3 py-2 sm:px-4 sm:py-3 text-left text-sm sm:text-base hover:bg-purple-50 focus:bg-purple-50 focus:outline-none transition-colors duration-150 ${
                index === 0 ? 'rounded-t-xl' : ''
              } ${
                index === Math.min(options.length - 1, 99) ? 'rounded-b-xl' : ''
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
    <div className="text-xs text-gray-500 mt-1">
      Showing {Math.min(options.length, 100)} of {options.length} results
    </div>
  </div>
);

export const LoadingSpinner = ({ message = 'Loading...' }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
      <p className="text-lg sm:text-xl text-gray-600 font-medium">{message}</p>
    </div>
  </div>
);

export const ErrorAlert = ({ error }) => (
  <div className="bg-red-50 border-l-4 border-red-400 p-4 m-4 sm:m-6 rounded-lg">
    <div className="flex items-center">
      <div className="flex-shrink-0">
        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm sm:text-base text-red-700 font-medium">{error}</p>
      </div>
    </div>
  </div>
);

export const InfoCard = ({ title, children, gradient = 'from-blue-50 to-indigo-50', borderColor = 'border-blue-200' }) => (
  <div className={`bg-gradient-to-r ${gradient} rounded-xl p-4 sm:p-6 border ${borderColor}`}>
    <div className="flex items-start">
      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="ml-3 sm:ml-4">
        <h4 className="text-base sm:text-lg font-semibold text-blue-800 mb-2">{title}</h4>
        <div className="text-sm sm:text-base text-blue-700 leading-relaxed">
          {children}
        </div>
      </div>
    </div>
  </div>
);

/**
 * Offline indicator component - shows connection status
 */
export const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showNotification, setShowNotification] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    // Subscribe to online/offline status changes
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);
      }
      setWasOffline(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      setShowNotification(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Show initial state if offline
    if (!isOnline) {
      setShowNotification(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wasOffline]);

  // Don't show anything if online and no notification
  if (isOnline && !showNotification) {
    return null;
  }

  return (
    <div
      className={`fixed top-4 right-4 left-4 sm:left-auto sm:w-96 z-50 transform transition-all duration-300 ${
        showNotification ? 'translate-y-0 opacity-100' : '-translate-y-20 opacity-0'
      }`}
    >
      <div
        className={`rounded-xl shadow-lg p-4 ${
          isOnline
            ? 'bg-green-50 border-l-4 border-green-400'
            : 'bg-yellow-50 border-l-4 border-yellow-400'
        }`}
      >
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {isOnline ? (
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          <div className="ml-3 flex-1">
            <p className={`text-sm font-medium ${isOnline ? 'text-green-800' : 'text-yellow-800'}`}>
              {isOnline ? 'Back Online' : 'Offline Mode'}
            </p>
            <p className={`text-sm mt-1 ${isOnline ? 'text-green-700' : 'text-yellow-700'}`}>
              {isOnline
                ? 'Connection restored. Fetching latest data...'
                : 'No internet connection. Using cached data.'}
            </p>
          </div>
          {!isOnline && (
            <button
              onClick={() => setShowNotification(false)}
              className="ml-3 flex-shrink-0 text-yellow-400 hover:text-yellow-600"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Install prompt component - shows "Add to Home Screen" prompt
 */
export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed or installed
    const dismissed = localStorage.getItem('installPromptDismissed');
    const installed = localStorage.getItem('appInstalled');
    
    if (dismissed || installed) {
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      // Prevent the default browser prompt
      e.preventDefault();
      
      // Store the event for later use
      setDeferredPrompt(e);
      
      // Show our custom prompt after a delay
      setTimeout(() => {
        setShowPrompt(true);
      }, 5000); // Show after 5 seconds
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for app installed event
    const handleAppInstalled = () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
      localStorage.setItem('appInstalled', 'true');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    // Show the browser's install prompt
    deferredPrompt.prompt();

    // Wait for user's choice
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log(`User ${outcome === 'accepted' ? 'accepted' : 'dismissed'} the install prompt`);

    if (outcome === 'accepted') {
      localStorage.setItem('appInstalled', 'true');
    }

    // Clear the prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('installPromptDismissed', 'true');
    
    // Show again after 7 days
    setTimeout(() => {
      localStorage.removeItem('installPromptDismissed');
    }, 7 * 24 * 60 * 60 * 1000);
  };

  if (!showPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slide-up">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl shadow-2xl p-5 text-white">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
              </svg>
            </div>
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-lg font-bold mb-1">Install Points Tool</h3>
            <p className="text-sm text-purple-100 mb-4">
              Install our app for quick access and offline use!
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleInstall}
                className="flex-1 bg-white text-purple-600 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-purple-50 transition-colors"
              >
                Install
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 rounded-lg font-semibold text-sm bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
              >
                Not Now
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="ml-2 text-purple-200 hover:text-white"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
